import React from 'react';
import { Link } from 'react-router-dom';
import {
    BookOpen,
    Target,
    Briefcase,
    Award,
    GraduationCap,
    Hospital,
    FlaskConical,
    ChevronRight
} from 'lucide-react';
import Footer from '../../../components/Footer';
import heroimg from '../../../assets/schools/pharma/M pharma P cology.jpg';
import HowToApplySection from '../../../components/HowToApplySection';

// Placeholder for importing images - replace with actual image paths
const heroImage = heroimg;

const MasterPharmacyPharmacology = () => {
    const programSections = [
        {
            title: "Program Outcomes",
            items: [
                "Demonstrate comprehensive understanding of drug action, mechanisms, pharmacokinetics, toxicology, and clinical pharmacology.",
                "Design and conduct preclinical and clinical pharmacological experiments using in-vitro and in-vivo models, with a strong emphasis on ethical practices.",
                "Apply pharmacological principles to the evaluation of drug safety, efficacy, and therapeutic potential in human and animal studies.",
                "Formulate research questions, plan and execute experiments, analyze data using statistical and scientific tools, and contribute to new knowledge in the field.",
                "Monitor, assess, and report adverse drug reactions, and contribute to post-marketing surveillance and drug safety regulations.",
                "Understand and apply national and international regulatory guidelines, ethical considerations, and GCP/GLP standards in research and drug development.",
                "Communicate effectively in scientific contexts through research publications, presentations, reports, and regulatory documentation.",
                "Work collaboratively in interdisciplinary teams and exhibit leadership in laboratory, clinical, or organizational settings.",
                "Analyze and solve complex problems in drug development, dosing, drug interactions, and therapeutic outcomes using evidence-based reasoning.",
                "Recognize the need for continuous professional development and stay updated with emerging trends and technologies in pharmacology and drug research."
            ],
            icon: <Target className="text-emerald-700" size={40} />
        },
        {
            title: "Career Prospects",
            items: [
                "Clinical Research Associate (CRA)",
                "Pharmacovigilance / Drug Safety Officer",
                "Preclinical Research Scientist",
                "Regulatory Affairs Specialist",
                "Toxicologist",
                "Medical Science Liaison (MSL) / Medical Advisor"
            ],
            icon: <Briefcase className="text-emerald-700" size={40} />
        }
    ];

    const uniqueSellingPoints = [
        "In-Depth Understanding of Drug Action",
        "Strong Emphasis on Clinical and Preclinical Research",
        "Gateway to Clinical Research and Pharmacovigilance",
        "Data-Driven Skill Set",
        "Versatile Career Opportunities",
        "Research-Intensive and Innovation-Oriented"
    ];

    return (
        <div className="bg-emerald-50">
            <div className="bg-white text-emerald-900 font-sans mx-4 md:mx-8 lg:mx-16 my-4 mt-[70px]">
                {/* Hero Section */}
                <div className="relative bg-gradient-to-r from-emerald-50 to-white">
                    <div className="container mx-auto px-4 py-16 md:py-24 flex flex-col-reverse md:flex-row items-center">
                        <div className="md:w-1/2 space-y-6">
                            <h1 className="text-4xl md:text-5xl font-bold text-emerald-800 leading-tight">
                                Master in Pharmacy (M.Pharm – Pharmacology)
                                <span className="block text-lg mt-2 font-medium text-emerald-600">
                                    Maya Devi University, Dehradun
                                </span>
                            </h1>
                            <p className="text-emerald-700 text-lg leading-relaxed">

                                The Master of Pharmacy (M.Pharm) in Pharmacology is a specialized postgraduate program that focuses on the study of the effects of drugs on biological systems and their therapeutic uses. Pharmacology plays a critical role in understanding how drugs interact with the body, how they produce their effects, and how their safety and efficacy can be optimized. This course prepares students to be experts in drug action, toxicity, therapeutic uses, and clinical pharmacology.


                            </p>
                            <div className="flex space-x-4">
                                <Link
                                    to="https://admission.maya.edu.in/"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-full 
                           transition duration-300 flex items-center group"
                                >
                                    Apply Now
                                    <ChevronRight
                                        className="ml-2 group-hover:translate-x-1 transition"
                                        size={20}
                                    />
                                </Link>
                            </div>
                        </div>
                        <div className="md:w-1/2 mb-8 md:mb-0 m-10">
                            <img
                                src={heroImage}
                                alt="Pharmacology Program"
                                className="rounded-xl shadow-lg hover:scale-105 transition duration-300"
                            />
                        </div>
                    </div>
                </div>

                {/* Eligibility and Program Details */}
                <div className="container mx-auto px-4 py-16 bg-emerald-50">
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <GraduationCap className="inline mr-3 text-emerald-600" size={30} />
                                Eligibility Criteria
                            </h2>
                            <ul className="space-y-3 text-emerald-700">
                                <li>• B.Pharm (Bachelor of Pharmacy) degree from a recognized institution</li>
                                <li>• Minimum 50% marks (or as per university requirements)</li>
                            </ul>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-emerald-800 mb-4">
                                <Hospital className="inline mr-3 text-emerald-600" size={30} />
                                Program Details
                            </h2>
                            <table className="w-full text-emerald-700">
                                <tbody>
                                    <tr>
                                        <td className="font-semibold">Duration:</td>
                                        <td>2 Years</td>
                                    </tr>
                                    <tr>
                                        <td className="font-semibold">Approved By:</td>
                                        <td>Pharmacy Council of India (PCI)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Program Sections */}
                <div className="container mx-auto px-4 py-16">
                    <div className="grid md:grid-cols-2 gap-8">
                        {programSections.map((section, index) => (
                            <div
                                key={index}
                                className="bg-emerald-50 p-6 rounded-xl shadow-md hover:shadow-lg transition"
                            >
                                <div className="flex items-center mb-4">
                                    {section.icon}
                                    <h3 className="text-2xl font-bold ml-4 text-emerald-800">
                                        {section.title}
                                    </h3>
                                </div>
                                <ul className="space-y-2 text-emerald-700">
                                    {section.items.map((item, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <span className="mr-2 text-emerald-600">•</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* USPs Section */}
                <div className="container mx-auto px-4 py-16">
                    <h2 className="text-3xl font-bold text-center text-emerald-800 mb-12">
                        Why Choose Us
                    </h2>
                    <div className="max-w-3xl mx-auto">
                        <div className="bg-emerald-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition">
                            <div className="text-emerald-600 text-4xl mb-6 flex justify-center">
                                <Award size={60} />
                            </div>
                            <h3 className="text-2xl font-bold text-emerald-800 text-center mb-6">
                                Our USPs
                            </h3>
                            <ul className="space-y-3">
                                {uniqueSellingPoints.map((usp, index) => (
                                    <li
                                        key={index}
                                        className="flex items-center text-emerald-700 text-lg"
                                    >
                                        <div className="mr-3 text-emerald-600">•</div>
                                        {usp}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {/* How to Apply Section */}
            <HowToApplySection/>
            <div className="bg-emerald-50">
                <Footer />
            </div>
        </div>
    );
};

export default MasterPharmacyPharmacology;